'use client';
import {default as Image} from '@/components/global/images/resized-cdn-image';
import { useParams } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';

import { doTranslation } from '@/app/i18n/client';
import Button from '@/components/global/buttons/button';

import styles from '@/components/home-page/home-page-news-updates.module.scss';
import drivenRewards from '@/public/assets/images/global/news-updates/driven-rewards.webp';
import readyForAdventure from '@/public/assets/images/global/news-updates/ready-for-adventure.webp';
import veteransHonorProgram from '@/public/assets/images/global/news-updates/veterans-honor-program.webp';
import sliderTrailwaysPets from '@/public/assets/images/global/news-updates/slider-trailways-pets.jpg';

import sliderNewService from '@/public/assets/images/global/news-updates/slider-new-service.webp';
import familyInKitchen from '@/public/assets/images/global/news-updates/slider-family-in-kitchen.webp';

interface Props {
  lng: string;
}

const HomePageNewsUpdates = (props: Props): React.JSX.Element => {
  const { lng }: any = useParams();
  const { t } = doTranslation(lng);
  let items = [
    {
      image: sliderTrailwaysPets,
      headline: t('Trailways of New York Goes Pet-Friendly'),
      link: '/news/trailways-new-york-pet-friendly/'
    },
    { image: drivenRewards, headline: t('Treat yourself'), link: '/my-driven-rewards/' },
    { image: readyForAdventure, headline: t('Ready for Adventure?'), link: '/packages/' },
    { image: veteransHonorProgram, headline: t('Our Promise of Support'), link: '/veteran-honors/' }
  ];
  if (process.env.NEXT_PUBLIC_COUNTRY == 'ca') {
    items = [
      {
        image: sliderNewService,
        headline: t('Trailways Announces New Service'),
        link: '/news/'
      },
      {
        image: familyInKitchen,
        headline: t('Time Together'),
        link: '/veteran-honors/'
      }
    ];
  }

  const [showMore, setShowMore] = useState(false);
  const validItems = items.filter((item) => item.image && item.headline && item.link);
  const visibleItems = showMore ? validItems : validItems.slice(0, 2);
  const showMoreButtonRef = useRef<HTMLButtonElement>(null);
  const learnMoreAnchorRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (showMore && learnMoreAnchorRef.current) {
      learnMoreAnchorRef.current.focus();
    }
  }, [showMore]);

  if (validItems.length === 0) {
    return <></>;
  }

  return (
    <>
      <div className={styles['news-updates']}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t('Discover What Makes Trailways Your Ultimate Travel Choice')}</h2>
          <p className={styles.subtitle}>{t('Stay up to date with the latest news and updates.')}</p>
        </div>
        <ul className={styles.posts}>
          {visibleItems.map((item: any, index: number) => (
            <li key={index} className={styles.post}>
              <div className={styles['image-wrapper']}>
                <Image alt="" aria-hidden="true" fill src={item.image.src} />
              </div>
              <div className={styles.content}>
                <h3 className={styles.headline}>{item.headline}</h3>
                <a
                  href={`${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${item.link}`}
                  className={`${styles.more} gtm-news_learn_more`}
                  ref={showMore && index === 2 ? learnMoreAnchorRef : undefined}
                >
                  <span className="sr-only">{item.headline}</span>
                  {t('Learn More')}
                </a>
              </div>
            </li>
          ))}
        </ul>
        {!showMore && validItems.length > 2 && (
          <div className={styles.action}>
            <Button
              ref={showMoreButtonRef}
              phantomDark={true}
              onClick={() => {
                setShowMore(true);
              }}
              id="gtm-news_show_more"
            >
              {t('Show More')}
            </Button>
          </div>
        )}
      </div>
      <div className={styles.contents}>
        <div className={styles['content-1']}>
          <h1 className={styles.title}>{t('Frequently Asked Questions About Bus Travel')}</h1>
          <p
            className={styles.text}
            dangerouslySetInnerHTML={{
              __html: t(
                'These are some of the most commonly asked <a>questions</a> about Trailways buses and bus travel. <a>Contact us</a> if you have any questions that you don’t see answered here.',
                { questions: '/faq/', contact: '/contacts/' }
              )
            }}
          />
        </div>
        <div className={styles['content-2']}>
          <h2 className={styles.subtitle}>{t('Are There Bathrooms on Trailways Buses?')}</h2>
          <p className={styles.text}>
            {t(
              'Yes. All of our buses have a bathroom for passenger use. You may also use the bathrooms at the facilities on layovers.'
            )}
          </p>
        </div>
        <div className={styles['content-2']}>
          <h2 className={styles.subtitle}>{t('What if Trailways Doesn’t Go to My Destination?')}</h2>
          <p className={styles.text}>
            {t(
              'We often coordinate with other bus carriers to ensure our passengers have additional travel options. You can find a bus route to get anywhere within the contiguous U.S. and parts of North America.'
            )}
          </p>
        </div>
        <div className={styles['content-2']}>
          <h2 className={styles.subtitle}>{t('Is There a Charge for Wifi?')}</h2>
          <p className={styles.text}>
            {t(
              'No, and most of our buses have power outlets and USB ports. So you can use and charge your phones, tablets, and computers as you use them.'
            )}
          </p>
        </div>
      </div>
    </>
  );
};

export default HomePageNewsUpdates;
